import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import useAssets from "../../../../../hooks/useAssets";
import useAssetModels from "../../../../../hooks/useAssetModels";

import { fetchAssetDetailsByChassis } from "../../../../../api/fetchAssets";
import { fetchExternalOccurrence, fetchOccurenceList } from "../../../../../api/fetchOccurence";
import { fetchSupportVehicle } from "../../../../../api/tas";
import { fetchVolksEssentialsContractDetails } from "../../../../../api/fetchContractDetails";

import { IChildForm } from "../../../";
import { Occurrence } from "../../../../../models/occurrence";
import { IContractDetailsVolksEssential } from "../../../../../models/volksPlanEssential";

interface IDialog {
    title: string;
    body: string;
}

interface IVolkstotalData {
    contractNumber: string;
    modality: string;
}

export const useVehicleForm = (props: IChildForm) => {
    const { getFilteredAssets } = useAssets();
    const [showLoadingDialog, setShowLoadingDialog] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState<IDialog | null>(null);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState<IDialog | null>(null);
    const [volkstotalData, setVolkstotalData] = useState<IVolkstotalData | null>(null);
    const [protocolNumber, setProtocolNumber] = useState<string | undefined>("");
    const [volksEssentialsPlan, setVolksEssentialsPlan] = useState<IContractDetailsVolksEssential | null>(null);

    const { assetModels } = useAssetModels();

    const intl = useIntl();

    const validateChassis = async (chassis: string) => {
        if (chassis.length === 17) {
            try {
                const assets = getFilteredAssets(chassis, 1, 0);
                const asset = assets.length > 0 ? assets[0] : null;

                setShowLoadingDialog(true);
                setLoadingMessage({
                    title: intl.formatMessage({ id: "intl-msg:appointment.step1.loadingVehicle" }),
                    body: intl.formatMessage({ id: "intl-msg:appointment.step1.loadingVehicle" }),
                });

                setVolkstotalData(null);

                const assetDetails = await fetchAssetDetailsByChassis(chassis);
                const tasVehicle = await fetchSupportVehicle(chassis);

                // Verificar se o veículo foi encontrado na RIO e no dynamics
                if (!assetDetails && !tasVehicle) {
                    setShowErrorDialog(true);
                    setErrorMessage({
                        title: intl.formatMessage({ id: "intl-msg:appointment.step1.errorLoadingVehicle" }),
                        body: intl.formatMessage({ id: "intl-msg:appointment.step1.errorLoadingVehicleBody" }),
                    });
                    return;
                }

                // Verifica se já existe chamado aberto para o veículo
                const existingOccurrence = await fetchOccurenceList({ chassisListFilter: [chassis] }, props.persona ?? "TOWER", 0, 1, false);
                if (existingOccurrence.occurrences.length > 0) {
                    setShowErrorDialog(true);
                    setErrorMessage({
                        title: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceExists" }),
                        body: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceExistsBody" }),
                    });
                    return;
                }

                // Encontrar o modelId correspondente ao modelCode do asset
                const modelObj = assetModels.find((model) => model.modelCode === assetDetails?.modelCode);
                let occurrenceData: Partial<Occurrence> = {
                    vehicleChassis: chassis,
                    vehicleModel: modelObj ? modelObj.modelId : undefined,
                    vehicleModelCode: assetDetails?.modelCode,
                    model: assetDetails?.model || modelObj?.description || tasVehicle?.model,
                    vehicleName: assetDetails?.name ?? chassis,
                    vehicleYear: assetDetails?.year,
                    vehicleLicensePlate: assetDetails?.licensePlate ?? tasVehicle?.licensePlate,
                    vehicleOdometer: assetDetails?.odometer,
                    vehicleHourmeter: assetDetails?.hourmeter,
                    vehicleId: assetDetails?.id,
                    vehicleType: asset?.type,
                    vehicleSaleDate: assetDetails?.saleDate ? 
                        new Date(assetDetails.saleDate) : 
                        tasVehicle?.billingDate ? 
                        new Date(tasVehicle.billingDate) : undefined,
                    customer: assetDetails?.accountName ?? tasVehicle?.vehicleOwner,
                    accountName: assetDetails?.accountName,
                };
                props.setOccurrence && props.setOccurrence(occurrenceData);

                const isValidContractValue = (v: any) =>
                    typeof v === 'string' && v.trim() !== '' && v !== 'null' && v !== 'undefined';
                const contractNumber = assetDetails?.volkstotal?.contractNumber ?? tasVehicle?.contractNumber;
                const contractModality = assetDetails?.volkstotal?.modality ?? tasVehicle?.contractModality;
                if (isValidContractValue(contractNumber) && isValidContractValue(contractModality)) {
                    setVolkstotalData({
                        contractNumber: contractNumber!,
                        modality: contractModality!,
                    });
                } else {
                    setVolkstotalData(null);
                }
            } catch (error: any) {
                setShowErrorDialog(true);
                setErrorMessage({
                    title: intl.formatMessage({ id: "intl-msg:appointment.step1.errorLoadingVehicle" }),
                    body: intl.formatMessage({ id: "intl-msg:appointment.step1.errorLoadingVehicleBody" }),
                });
            } finally {
                setShowLoadingDialog(false);
            }
        }
    }

    const formatProtocolNumber = (value: string) => {
        let raw = value
            .replace(/^OCOR-?/i, "")
            .replace(/[^A-Z0-9]/gi, "")
            .toUpperCase()
            .slice(0, 12);

        const firstPart = raw.slice(0, 6);
        const secondPart = raw.slice(6, 12);

        let formatted = "OCOR-" + firstPart;

        if (secondPart) {
            formatted += "-" + secondPart;
        }

        setProtocolNumber(formatted);
    };

    useEffect(() => {
        const fetchOccurrenceData = async () => {
            if (protocolNumber && protocolNumber.length === 18) {
                setShowLoadingDialog(true);
                setLoadingMessage({
                    title: intl.formatMessage({ id: "intl-msg:appointment.step1.loadingOccurrence" }),
                    body: intl.formatMessage({ id: "intl-msg:appointment.step1.loadingOccurrence" }),
                });

                try {
                    const externalOccurrence = await fetchExternalOccurrence(protocolNumber);
                    
                    if (externalOccurrence) {
                        let occurrence: Partial<Occurrence> = {
                            vehicleChassis: externalOccurrence.vehicleChassis,
                            vehicleModel: externalOccurrence.vehicleModel,
                            vehicleModelCode: externalOccurrence.vehicleModelCode,
                            model: externalOccurrence.model,
                            vehicleName: externalOccurrence.vehicleName,
                            vehicleYear: externalOccurrence.vehicleYear,
                            vehicleLicensePlate: externalOccurrence.vehicleLicensePlate,
                            vehicleOdometer: externalOccurrence.vehicleOdometer,
                            vehicleHourmeter: externalOccurrence.vehicleHourmeter,
                            vehicleId: externalOccurrence.vehicleId,
                            vehicleType: externalOccurrence.vehicleType,
                            vehicleSaleDate: externalOccurrence.vehicleSaleDate,
                            customer: externalOccurrence.customer,
                            accountName: externalOccurrence.accountName,
                        };
                        props.setOccurrence && props.setOccurrence(occurrence);
                    }
                } catch (error: any) {
                    setShowErrorDialog(true);

                    const data = error.response.data;
                    if (data?.code === '404') {
                        setErrorMessage({
                            title: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceNotFound" }),
                            body: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceNotFoundBody" }),
                        });
                    } 
                    
                    if (data?.code === '409') {
                        setErrorMessage({
                            title: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceAlreadyExists" }),
                            body: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceAlreadyExistsBody" }),
                        });
                    }

                    if (data?.code === '400' || data?.code === '500') {
                        setErrorMessage({
                            title: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceInvalid" }),
                            body: intl.formatMessage({ id: "intl-msg:appointment.step1.errorOccurrenceInvalidBody" }),
                        });
                    }
                } finally {
                    setShowLoadingDialog(false);
                }
            }
        }

        fetchOccurrenceData();
    }, [protocolNumber]);
    
    useEffect(() => {
        const fetchVolksEssentialsPlanData = async () => {
            const volksEssentialsPlanSummary = 
                props.occurrence?.volksEssentialsPlanContract ? 
                    await fetchVolksEssentialsContractDetails({ contract: props.occurrence?.volksEssentialsPlanContract }) :
                    await fetchVolksEssentialsContractDetails({ chassis: props.occurrence?.vehicleChassis });

            if (volksEssentialsPlanSummary) {
                const volksEssentialsPlanDetails = await fetchVolksEssentialsContractDetails({ id: volksEssentialsPlanSummary.id });
                setVolksEssentialsPlan({
                    ...volksEssentialsPlanDetails,
                    revision: volksEssentialsPlanSummary.revision?.filter((revision) => revision.available || revision.occurrenceId === props.occurrence?.id)
                });
            }
        }
        fetchVolksEssentialsPlanData();
    }, [props.occurrence?.vehicleChassis, props.occurrence?.volksEssentialsPlanContract, props.refetchKey]);

    return {
        showLoadingDialog,
        loadingMessage,
        showErrorDialog,
        errorMessage,
        volkstotalData,
        assetModels,
        protocolNumber,
        volksEssentialsPlan,

        setShowErrorDialog,
        validateChassis,
        formatProtocolNumber,
    }
};